import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { Driver, DriverStateTypes } from "./Driver.util";

const name = "driver";
const extraActions = createExtraActions();

const DriverInitialState: DriverStateTypes = {
  createDriver: ResetApiState(null),
  resetCreateDriver: ResetApiState(null),
  editDriver: ResetApiState(null),
  resetEditDriver: ResetApiState(null),
  deleteDriver: ResetApiState(null),
  resetDeleteDriver: ResetApiState(null),
  drivers: ResetApiState([]),
  pagedDrivers: ResetApiState(ResetPaginationState()),
  driver: ResetApiState(null),
  resetDriver: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createDriver = createAsyncThunk<ResponseWrapper<Driver>, any>(
    `/create-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateDriver = createAction(`/reset-create-${name}`);

  const editDriver = createAsyncThunk<ResponseWrapper<Driver>, any>(
    `/edit-${name}`,
    async ({ data, id }: any, thunkApi) =>
      axios
        .put(`/${name}/${id}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditDriver = createAction(`/reset-edit-${name}`);

  const deleteDriver = createAsyncThunk<number, any>(
    `/delete-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`${name}/${id}`)
        .then((response) => {
          if (response.data.success) {
            return id;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error?.response?.data);
        })
  );

  const resetDeleteDriver = createAction(`/reset-delete-${name}`);

  const getDrivers = createAsyncThunk<Driver[]>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedDrivers = createAsyncThunk<PaginationType<Driver[]>, any>(
    `/get-paged-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}/paged${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getDriver = createAsyncThunk<Driver, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}/${id}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetDriver = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    createDriver,
    resetCreateDriver,
    editDriver,
    resetEditDriver,
    deleteDriver,
    resetDeleteDriver,
    getDrivers,
    getPagedDrivers,
    getDriver,
    resetDriver,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<DriverStateTypes>
) {
  return {
    ...createDriverReducer(),
    ...resetCreateDriver(),
    ...editDriverReducer(),
    ...resetEditDriver(),
    ...deleteDriverReducer(),
    ...resetDeleteDriver(),
    ...driversReducer(),
    ...pagedDriversReducer(),
    ...driverReducer(),
    ...resetDriver(),
    ...resetStatesReducer(),
  };

  function createDriverReducer() {
    return {
      ...builder.addCase(extraActions.createDriver.pending, (state, action) => {
        state.createDriver = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.createDriver.fulfilled,
        (state, action) => {
          state.createDriver = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createDriver.rejected,
        (state, action) => {
          state.createDriver = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateDriver() {
    return {
      ...builder.addCase(extraActions.resetCreateDriver, (state, action) => {
        state.resetCreateDriver = ResetApiState(true);
        state.createDriver = ResetApiState(null);
      }),
    };
  }

  function editDriverReducer() {
    return {
      ...builder.addCase(extraActions.editDriver.pending, (state, action) => {
        state.editDriver = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.editDriver.fulfilled, (state, action) => {
        state.editDriver = {
          loading: false,
          payload: action.payload.message,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.editDriver.rejected, (state, action) => {
        state.editDriver = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetEditDriver() {
    return {
      ...builder.addCase(extraActions.resetEditDriver, (state, action) => {
        state.resetEditDriver = ResetApiState(true);
        state.editDriver = ResetApiState(null);
      }),
    };
  }

  function deleteDriverReducer() {
    return {
      ...builder.addCase(extraActions.deleteDriver.pending, (state, action) => {
        state.deleteDriver = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.deleteDriver.fulfilled,
        (state, action) => {
          state.deleteDriver = {
            loading: false,
            payload: true,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteDriver.rejected,
        (state, action) => {
          state.deleteDriver = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteDriver() {
    return {
      ...builder.addCase(extraActions.resetDeleteDriver, (state, action) => {
        state.resetDeleteDriver = ResetApiState(true);
        state.deleteDriver = ResetApiState(null);
      }),
    };
  }

  function driversReducer() {
    return {
      ...builder.addCase(extraActions.getDrivers.pending, (state, action) => {
        state.drivers = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getDrivers.fulfilled, (state, action) => {
        state.drivers = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getDrivers.rejected, (state, action) => {
        state.drivers = {
          loading: false,
          payload: [],
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function pagedDriversReducer() {
    return {
      ...builder.addCase(
        extraActions.getPagedDrivers.pending,
        (state, action) => {
          state.pagedDrivers = {
            loading: true,
            payload: ResetPaginationState(),
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedDrivers.fulfilled,
        (state, action) => {
          state.pagedDrivers = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedDrivers.rejected,
        (state, action) => {
          state.pagedDrivers = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function driverReducer() {
    return {
      ...builder.addCase(extraActions.getDriver.pending, (state, action) => {
        state.driver = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getDriver.fulfilled, (state, action) => {
        state.driver = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getDriver.rejected, (state, action) => {
        state.driver = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetDriver() {
    return {
      ...builder.addCase(extraActions.resetDriver, (state, action) => {
        state.resetDriver = ResetApiState(true);
        state.driver = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createDriver = ResetApiState(null);
        state.resetCreateDriver = ResetApiState(null);
        state.editDriver = ResetApiState(null);
        state.resetEditDriver = ResetApiState(null);
        state.deleteDriver = ResetApiState(null);
        state.resetDeleteDriver = ResetApiState(null);
        state.drivers = ResetApiState([]);
        state.driver = ResetApiState(null);
        state.resetDriver = ResetApiState(null);
        state.resetDriver = ResetApiState(null);
      }),
    };
  }
}

const DriverSlice = createSlice({
  name,
  initialState: DriverInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const DriverActions = {
  ...DriverSlice.actions,
  ...extraActions,
};
export const DriverReducer = DriverSlice.reducer;
